import { useEffect, useState } from "react";
import { fetchQuote } from "../lib/api";

/**
 * Encapsulates fetching the latest quote for `ticker` and cancelling
 * stale promises when the dependency tuple changes.
 *
 * Re-fetches whenever (ticker, enabled) changes. When `enabled` is false
 * the hook is inert and returns the initial empty state.
 *
 * @param {Object|null} ticker  Slickcharts ticker row (must expose `.symbol`)
 * @param {boolean}     enabled Gate (e.g. drawer open flag)
 * @returns {{ quote: Object|null, error: Error|null, loading: boolean }}
 */
export function useQuote(ticker, enabled) {
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticker || !enabled) {
      setQuote(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setQuote(null);
    setError(null);
    setLoading(true);

    fetchQuote(ticker.symbol)
      .then((d) => {
        if (!cancelled) setQuote(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticker, enabled]);

  return { quote, error, loading };
}
